'use client';

import { deleteCookie, getCookie, setCookie } from 'cookies-next/client';
import { useCallback, useEffect, useState } from 'react';
import type { CookieStorageItems } from '@/core/models/cookie-storage';
import { ClientCookies, ItemTypes } from '@/core/services/cookie-storage';

const getCookieValue = <K extends keyof CookieStorageItems>(
  key: K,
): CookieStorageItems[K] | null => {
  const value = getCookie(key);
  if (value === undefined) return null;

  const type = ItemTypes[key];
  if (type === 'string') {
    return String(value) as CookieStorageItems[K];
  }
  if (type === 'number') {
    return Number(value) as unknown as CookieStorageItems[K];
  }
  if (type === 'boolean') {
    return Boolean(value) as unknown as CookieStorageItems[K];
  }
  if (type === 'object') {
    try {
      return JSON.parse(String(value)) as CookieStorageItems[K];
    } catch {
      return String(value) as unknown as CookieStorageItems[K];
    }
  }
  throw new Error(`Invalid type for key ${key}`);
};

const useCookieStorageState = <K extends keyof CookieStorageItems>(
  key: K,
  defaultValue: CookieStorageItems[K] | null,
) => {
  const [cookieValue = getCookieValue<K>(key), setCookieValue] = useState<
    CookieStorageItems[K] | null
  >(getCookieValue<K>(key));

  const setState = useCallback(
    (value: CookieStorageItems[K] | null) => {
      if (value === null) {
        deleteCookie(key);
        setCookieValue(value);
        return;
      }

      const type = ItemTypes[key];
      if (type === 'object') {
        setCookie(key, JSON.stringify(value));
      } else {
        setCookie(key, value);
      }
      setCookieValue(value);
    },
    [key],
  );

  useEffect(
    () =>
      ClientCookies.subscribe(key, value => {
        setCookieValue(value);
      }),
    [key],
  );

  return [cookieValue ?? defaultValue, setState] as const;
};

export default useCookieStorageState;
